import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import './Gallery.css';

const Gallery = () => {
    const navigate = useNavigate();
    const { showToast, showConfirm } = useToast();
    const [galleryItems, setGalleryItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [formData, setFormData] = useState({
        heading: '',
        images: []
    });
    const [previewImages, setPreviewImages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addMoreImages, setAddMoreImages] = useState({ galleryId: null, images: [], previews: [] });
    const [isAddingImages, setIsAddingImages] = useState(false);

    const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
    const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

    // Fetch gallery items
    const fetchGalleryItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/get_gallery.php`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success && result.gallery) {
                setGalleryItems(result.gallery);
            } else {
                setGalleryItems([]);
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            showToast({ message: 'Error fetching gallery: ' + error.message, type: 'error' });
            setGalleryItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchGalleryItems();
    }, [fetchGalleryItems]);

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...files]
        }));

        // Create preview URLs
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImages(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removePreviewImage = (index) => {
        setPreviewImages(prev => prev.filter((_, i) => i !== index));
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.heading.trim()) {
            showToast({ message: 'Please enter a heading for the gallery', type: 'error' });
            return;
        }

        if (formData.images.length === 0) {
            showToast({ message: 'Please select at least one image', type: 'error' });
            return;
        }

        setIsSubmitting(true);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('heading', formData.heading.trim());

            formData.images.forEach((image, index) => {
                uploadFormData.append(`images[]`, image);
            });

            const response = await fetch(`${API_BASE_URL}/add_gallery.php`, {
                method: 'POST',
                body: uploadFormData
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Upload failed');
            }

            showToast({ message: 'Gallery uploaded successfully!', type: 'success' });

            // Reset form
            setFormData({ heading: '', images: [] });
            setPreviewImages([]);
            setShowUploadForm(false);

            // Refresh gallery
            await fetchGalleryItems();
        } catch (error) {
            console.error('Upload Error:', error);
            showToast({ message: 'Error uploading gallery: ' + error.message, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteImage = async (galleryId, imagePath) => {
        try {
            const confirmed = await showConfirm('Are you sure you want to delete this image?', {
                title: 'Delete Image',
                confirmText: 'Delete',
                cancelText: 'Cancel'
            });

            if (!confirmed) return;

            const response = await fetch(`${API_BASE_URL}/delete_gallery_image.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ gallery_id: galleryId, image_path: imagePath })
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Delete failed');
            }

            showToast({ message: 'Image deleted successfully', type: 'success' });
            await fetchGalleryItems();
        } catch (error) {
            showToast({ message: 'Error deleting image: ' + error.message, type: 'error' });
        }
    };

    const handleDeleteGallery = async (galleryId) => {
        try {
            const confirmed = await showConfirm('Are you sure you want to delete this entire gallery?', {
                title: 'Delete Gallery',
                confirmText: 'Delete',
                cancelText: 'Cancel'
            });

            if (!confirmed) return;

            const response = await fetch(`${API_BASE_URL}/delete_gallery.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: galleryId })
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Delete failed');
            }

            showToast({ message: 'Gallery deleted successfully', type: 'success' });
            await fetchGalleryItems();
        } catch (error) {
            showToast({ message: 'Error deleting gallery: ' + error.message, type: 'error' });
        }
    };

    const handleAddMoreImagesChange = (e) => {
        const files = Array.from(e.target.files);

        setAddMoreImages(prev => ({
            ...prev,
            images: [...prev.images, ...files]
        }));

        // Create preview URLs
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAddMoreImages(prev => ({
                    ...prev,
                    previews: [...prev.previews, reader.result]
                }));
            };
            reader.readAsDataURL(file);
        });
    };

    const removeAddMorePreviewImage = (index) => {
        setAddMoreImages(prev => ({
            ...prev,
            previews: prev.previews.filter((_, i) => i !== index),
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleAddMoreImagesSubmit = async (e) => {
        e.preventDefault();

        if (addMoreImages.images.length === 0) {
            showToast({ message: 'Please select at least one image', type: 'error' });
            return;
        }

        setIsAddingImages(true);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('gallery_id', addMoreImages.galleryId);

            addMoreImages.images.forEach((image) => {
                uploadFormData.append(`images[]`, image);
            });

            const response = await fetch(`${API_BASE_URL}/add_more_gallery_images.php`, {
                method: 'POST',
                body: uploadFormData
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Upload failed');
            }

            showToast({ message: 'Images added successfully!', type: 'success' });

            // Reset add more form
            setAddMoreImages({ galleryId: null, images: [], previews: [] });

            // Refresh gallery
            await fetchGalleryItems();
        } catch (error) {
            console.error('Upload Error:', error);
            showToast({ message: 'Error adding images: ' + error.message, type: 'error' });
        } finally {
            setIsAddingImages(false);
        }
    };

    return (
        <div className="gallery-container">
            {/* Header */}
            <div className="gallery-header">
                <div className="gallery-header-content">
                    <div className="gallery-header-left">
                        <div className="gallery-header-icon">
                            <svg viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                                <path d="M21 15l-5-5L7 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <h1>Gallery Management</h1>
                            <p className="gallery-header-subtitle">Manage and upload gallery images for your projects</p>
                        </div>
                    </div>
                    <div className="gallery-header-buttons">
                        <button
                            className="back-btn"
                            onClick={() => navigate('/dashboard')}
                            title="Back to Dashboard"
                        >
                            ← Back
                        </button>
                        <button
                            className="upload-btn"
                            onClick={() => setShowUploadForm(!showUploadForm)}
                        >
                            <span className="btn-plus">+</span>
                            {showUploadForm ? 'Cancel' : 'Upload Gallery'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
                <div className="upload-form-container">
                    <form onSubmit={handleSubmit} className="upload-form">
                        <div className="form-group">
                            <label htmlFor="heading">Gallery Title</label>
                            <input
                                type="text"
                                id="heading"
                                placeholder="Enter gallery title (e.g., Villa Construction Phase 1)"
                                value={formData.heading}
                                onChange={(e) => setFormData(prev => ({ ...prev, heading: e.target.value }))}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Upload Images</label>
                            <div className="file-input-wrapper">
                                <input
                                    type="file"
                                    id="images"
                                    multiple
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="file-input"
                                />
                                <label htmlFor="images" className="file-input-label">
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p>Click to upload or drag and drop</p>
                                    <p className="file-input-hint">PNG, JPG, GIF up to 10MB</p>
                                </label>
                            </div>
                        </div>

                        {/* Image Previews */}
                        {previewImages.length > 0 && (
                            <div className="preview-container">
                                <h3>Image Previews ({previewImages.length})</h3>
                                <div className="preview-grid">
                                    {previewImages.map((preview, index) => (
                                        <div key={index} className="preview-item">
                                            <img src={preview} alt={`Preview ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-preview-btn"
                                                onClick={() => removePreviewImage(index)}
                                                title="Remove image"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Uploading...' : 'Upload Gallery'}
                            </button>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => {
                                    setShowUploadForm(false);
                                    setFormData({ heading: '', images: [] });
                                    setPreviewImages([]);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading gallery...</p>
                </div>
            )}

            {/* Gallery Items */}
            {!isLoading && galleryItems.length === 0 && !showUploadForm && (
                <div className="empty-state">
                    <div className="empty-icon">🖼️</div>
                    <h2>No gallery items yet</h2>
                    <p>Start by uploading your first gallery</p>
                    <button
                        className="empty-upload-btn"
                        onClick={() => setShowUploadForm(true)}
                    >
                        Upload Gallery
                    </button>
                </div>
            )}

            {!isLoading && galleryItems.length > 0 && (
                <div className="gallery-items-section">
                    <h2>Gallery Collections</h2>
                    <div className="gallery-collections">
                        {galleryItems.map(item => (
                            <div key={item.id} className="gallery-collection">
                                <div className="collection-header">
                                    <h3>{item.heading}</h3>
                                    <div className="collection-actions">
                                        <button
                                            className="submit-btn"
                                            onClick={() => setAddMoreImages({ galleryId: item.id, images: [], previews: [] })}
                                            title="Add more images"
                                        >

                                            +
                                        </button>
                                        <button
                                            className="delete-collection-btn"
                                            onClick={() => handleDeleteGallery(item.id)}
                                            title="Delete entire gallery"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none">
                                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Add More Images Form */}
                                {addMoreImages.galleryId === item.id && (
                                    <div className="add-more-images-form">
                                        <form onSubmit={handleAddMoreImagesSubmit}>
                                            <div className="form-group">
                                                <label>Add More Images</label>
                                                <div className="file-input-wrapper">
                                                    <input
                                                        type="file"
                                                        id={`add-images-${item.id}`}
                                                        multiple
                                                        accept="image/*"
                                                        onChange={handleAddMoreImagesChange}
                                                        className="file-input"
                                                    />
                                                    <label htmlFor={`add-images-${item.id}`} className="file-input-label">
                                                        <svg viewBox="0 0 24 24" fill="none">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <p>Click to upload or drag and drop</p>
                                                        <p className="file-input-hint">PNG, JPG, GIF up to 10MB</p>
                                                    </label>
                                                </div>
                                            </div>

                                            {addMoreImages.previews.length > 0 && (
                                                <div className="preview-container">
                                                    <h3>Image Previews ({addMoreImages.previews.length})</h3>
                                                    <div className="preview-grid">
                                                        {addMoreImages.previews.map((preview, index) => (
                                                            <div key={index} className="preview-item">
                                                                <img src={preview} alt={`Preview ${index + 1}`} />
                                                                <button
                                                                    type="button"
                                                                    className="remove-preview-btn"
                                                                    onClick={() => removeAddMorePreviewImage(index)}
                                                                    title="Remove image"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="form-actions">
                                                <button
                                                    type="submit"
                                                    className="submit-btn"
                                                    disabled={isAddingImages}
                                                >
                                                    {isAddingImages ? 'Adding...' : 'Add Images'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cancel-btn"
                                                    onClick={() => setAddMoreImages({ galleryId: null, images: [], previews: [] })}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                <div className="collection-images">
                                    {item.images && item.images.map((image, index) => (
                                        <div key={index} className="gallery-image-item">
                                            <img
                                                src={`${BASE_URL}${image.url}`}
                                                alt={`${item.heading} - ${index + 1}`}
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3C/svg%3E';
                                                }}
                                            />
                                            <button
                                                className="delete-image-btn"
                                                onClick={() => handleDeleteImage(item.id, image.url)}
                                                title="Delete image"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none">
                                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;